import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { InputField } from '@/components/ui/input-field';
import { Checkbox } from '@/components/ui/checkbox';
import { useFormValidation, emailPattern, passwordValidator } from '@/hooks/useFormValidation';
import { useAuth, type SignupRole } from '@/hooks/useAuth';
import { describePasswordFailure, PASSWORD_MIN_LENGTH } from '@/lib/adminPasswordPolicy';
import { useToast } from '@/hooks/use-toast';
import { phoneValidationError } from '@/lib/clientInputValidation';
import { TERMS_VERSION } from '@/lib/legalConsent';
import { defaultChoices, signupConsentsMetadata, type ConsentChoices } from '@/lib/consents';
import { ConsentChoicesFields } from '@/components/consent/ConsentChoicesFields';
import { useCountryConfig } from '@/lib/countryConfig';
import { KanfyLogo } from '@/components/KanfyLogo';
import { IS_NATIVE as isNative } from '@/platform';
import {
  Mail,
  Lock,
  User,
  ArrowLeft,
  Eye,
  EyeOff,
  Shield,
  Loader2,
} from 'lucide-react';

export type AuthStep = 'main' | 'forgot-password' | 'email-code';
export type EmailView = 'signin' | 'signup' | 'magic';

/** Supabase email codes are 6 digits by default and at most 10 (mailer_otp_length). */
const EMAIL_CODE = /^\d{6,10}$/;

export interface AuthFormProps {
  /** Which email panel to open on ('signin' unless the caller has a reason to start elsewhere, e.g. Auth.tsx's ?mode=signup). */
  initialEmailView?: EmailView;
  /** Called after a successful sign-in (password or emailed code), sign-up, or social auth. The modal closes itself; the page navigates. */
  onSuccess: () => void;
  /** Whether to show the "Welcome to Kanfy" logo/heading block. The page wrapper renders its own top-bar logo and can turn this off to avoid doubling up. */
  showHeader?: boolean;
  /**
   * Which kind of account this is, ONLY for the rare caller that already
   * genuinely knows — e.g. a deep link back into an in-progress helper
   * application. Passed straight through to signUp() with no visible UI here
   * (no banner, no switch): an ordinary signup shows no role concept at all
   * and is deferred to the post-signup /profile-creation chooser instead.
   * Undefined here means undeclared, NOT client — see useAuth.ts's signUp().
   */
  signupRole?: SignupRole;
}

/**
 * The one canonical sign-in/sign-up form, shared by the inline modal
 * (AuthModal) and the full-page route (/auth, /login, /signup) so the two
 * entry points render identically instead of drifting into two different
 * experiences. Ported from the modal's implementation, which was the more
 * complete of the app's three prior auth components (validation, magic
 * link, forgot-password, duplicate-account handling) and the only one using
 * current design tokens throughout.
 *
 * Sessions persist until sign-out. The form does not offer a remember-me
 * choice because the shared client does not vary storage by that preference.
 */
export function AuthForm({ initialEmailView = 'signin', onSuccess, showHeader = true, signupRole }: AuthFormProps) {
  const [currentStep, setCurrentStep] = useState<AuthStep>('main');
  const [emailView, setEmailView] = useState<EmailView>(initialEmailView);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  // Reminders on, offers off, nothing agreed abroad until ticked (legal review §6).
  const [consents, setConsents] = useState<ConsentChoices>(defaultChoices);
  const [isLoading, setIsLoading] = useState(false);

  const { signUp, signIn, signInWithGoogle, signInWithMagicLink, verifyEmailCode, resetPassword } = useAuth();
  // The address the sign-in email went to, while its code is awaited.
  const [codeEmail, setCodeEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const { toast } = useToast();
  const { config } = useCountryConfig();

  const loginForm = useFormValidation(
    { email: '', password: '' },
    {
      email: { required: true, pattern: emailPattern },
      password: { required: true },
    }
  );

  const signupForm = useFormValidation(
    { firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '' },
    {
      firstName: { required: true, minLength: 2 },
      lastName: { required: true, minLength: 2 },
      email: { required: true, pattern: emailPattern },
      phone: { required: false, custom: (value) => phoneValidationError(value) },
      password: { required: true, custom: passwordValidator },
      confirmPassword: {
        required: true,
        dependencies: ['password'],
        custom: (value, values) => (value !== values?.password ? 'Passwords do not match' : null),
      },
    }
  );

  const magicLinkForm = useFormValidation(
    { email: '' },
    { email: { required: true, pattern: emailPattern } }
  );

  const forgotPasswordForm = useFormValidation(
    { email: '' },
    { email: { required: true, pattern: emailPattern } }
  );

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginForm.validateAll()) return;
    setIsLoading(true);
    try {
      const { error } = await signIn(loginForm.values.email, loginForm.values.password);
      if (error) {
        let msg = error.message;
        if (msg.includes('Invalid login credentials')) {
          msg = 'Invalid email or password. Please check your credentials and try again.';
        } else if (msg.includes('Email not confirmed')) {
          msg = 'Please check your email and confirm your account before signing in.';
        } else if (msg.includes('rate limit') || msg.includes('too many')) {
          msg = 'Too many attempts. Please wait 15 minutes before trying again.';
        }
        toast({ title: 'Sign in failed', description: msg, variant: 'destructive' });
      } else {
        toast({ title: 'Welcome back!', description: 'You have successfully signed in.' });
        onSuccess();
      }
    } catch {
      toast({ title: 'Network error', description: 'Could not reach the server. Please check your connection.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signupForm.validateAll()) return;
    if (!agreeToTerms) {
      toast({ title: 'Terms required', description: 'Please agree to the Terms of Use to continue.', variant: 'destructive' });
      return;
    }
    if (!consents.transferAbroad) {
      toast({ title: 'Agreement required', description: 'Kanfy keeps accounts with providers outside your country. Please agree to continue.', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await signUp(
        signupForm.values.email,
        signupForm.values.password,
        signupForm.values.firstName,
        signupForm.values.lastName,
        signupForm.values.phone || undefined,
        // The prop, not a form choice: this screen shows no role concept at
        // all. Undefined here reaches useAuth's signUp() as undeclared, which
        // is what sends a plain signup to /profile-creation to choose later.
        signupRole,
        TERMS_VERSION,
        signupConsentsMetadata(consents),
      );
      if (error) {
        let msg = error.message;
        const isDuplicate =
          msg.includes('User already registered') ||
          msg.includes('already registered') ||
          msg.includes('duplicate') ||
          (error as { code?: string })?.code === 'user_already_exists';
        if (isDuplicate) {
          loginForm.setValue('email', signupForm.values.email || '');
          setEmailView('signin');
          toast({
            title: 'Account already exists',
            description: "An account with this email already exists. We've switched you to sign in — just enter your password.",
          });
          return;
        } else if (msg.includes('Password should be at least')) {
          msg = describePasswordFailure(signupForm.values.password) ?? 'Please choose a stronger password.';
        }
        toast({ title: 'Sign up failed', description: msg, variant: 'destructive' });
      } else {
        /*
         * No success toast here — see the long note in useAuth.ts's signUp().
         * With Supabase's email-enumeration protection on, re-registering an
         * already-confirmed address lands HERE, in the success branch, having
         * created nothing and emailed nothing. The duplicate check above
         * cannot catch it because no error is returned at all.
         *
         * signUp() already raises its own honest toast for this, so the
         * second, more confident one that used to fire here is gone rather
         * than reworded: two toasts for one action was noise even when the
         * wording was right.
         */
        onSuccess();
      }
    } catch {
      toast({ title: 'Network error', description: 'Could not reach the server. Please check your connection.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!magicLinkForm.validateAll()) return;
    setIsLoading(true);
    try {
      const { error } = await signInWithMagicLink(magicLinkForm.values.email);
      if (error) {
        let msg = error.message;
        if (msg.includes('rate limit')) {
          msg = 'Too many requests. Please wait 15 minutes before requesting another link.';
        }
        toast({ title: 'Magic link failed', description: msg, variant: 'destructive' });
      } else {
        // Not signed in yet: the email's code is typed here. Its link only
        // finishes in the browser that asked for it, and on Android it never
        // opens the app (owner, 31 Oct), so the code is the way that always works.
        setCodeEmail(magicLinkForm.values.email.trim().toLowerCase());
        setEmailCode('');
        setCurrentStep('email-code');
      }
    } catch {
      toast({ title: 'Network error', description: 'Could not reach the server. Please check your connection.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_CODE.test(emailCode)) return;
    setIsLoading(true);
    try {
      const { error } = await verifyEmailCode(codeEmail, emailCode);
      if (error) {
        const msg = /expired|invalid/i.test(error.message)
          ? 'That code is wrong or has expired. Check the latest email, or send a new one.'
          : error.message;
        toast({ title: 'Sign in failed', description: msg, variant: 'destructive' });
      } else {
        toast({ title: 'Welcome!', description: 'You are signed in.' });
        onSuccess();
      }
    } catch {
      toast({ title: 'Network error', description: 'Could not reach the server. Please check your connection.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const resendEmailCode = async () => {
    setIsLoading(true);
    try {
      const { error } = await signInWithMagicLink(codeEmail);
      if (!error) setEmailCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotPasswordForm.validateAll()) return;
    setIsLoading(true);
    try {
      const { error } = await resetPassword(forgotPasswordForm.values.email);
      if (error) {
        toast({ title: 'Reset failed', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Reset link sent!', description: 'Check your email for password reset instructions.' });
        setCurrentStep('main');
      }
    } catch {
      toast({ title: 'Network error', description: 'Could not reach the server. Please check your connection.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        let msg = error.message;
        if (msg.includes('access_denied')) {
          msg = 'Google sign-in was cancelled. Please try again.';
        }
        toast({ title: 'Google sign in failed', description: msg, variant: 'destructive' });
      } else {
        onSuccess();
      }
    } catch {
      toast({ title: 'Network error', description: 'Could not reach the server. Please check your connection.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const renderMainScreen = () => (
    <div className="space-y-5">
      {showHeader && (
        <div className="text-center space-y-1.5">
          {/* Real mark, not the retired "TH" lettermark. */}
          <div className="flex items-center justify-center">
            <KanfyLogo variant="primary" size="lg" />
          </div>
          <h2 className="h-section">Welcome to Kanfy</h2>
          <p className="text-sm text-muted-foreground">Join our growing community of customers and helpers</p>
        </div>
      )}

      {/* Google only now — one OAuth decision instead of two on the busiest
          screen in the app. */}
      <button
        type="button"
        className="w-full flex items-center justify-center gap-3 rounded-xl border border-border bg-card py-3 shadow-sm transition-colors hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-50 text-foreground"
        onClick={handleGoogleAuth}
        disabled={isLoading}
        aria-label="Sign in with Google"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <span className="font-medium">Continue with Google</span>
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
        </div>
      </div>

      {/* Inline email auth — no tab strip between sign-in/sign-up, but
          Password/Magic-link IS a real toggle now (was a buried text link
          reachable only from the password screen), since magic link is a
          first-class way in, not a fallback for people who forgot their
          password. Same signIn/signInWithMagicLink wiring either way — this
          only changes which screen the client sees. */}
      {(emailView === 'signin' || emailView === 'magic') && (
        <div className="space-y-3">
          <div className="flex rounded-xl bg-muted p-1" role="tablist" aria-label="Sign-in method">
            <button
              type="button"
              role="tab"
              aria-selected={emailView === 'signin'}
              className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-colors ${
                emailView === 'signin' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
              onClick={() => setEmailView('signin')}
              disabled={isLoading}
            >
              Password
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={emailView === 'magic'}
              className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-colors ${
                emailView === 'magic' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
              onClick={() => setEmailView('magic')}
              disabled={isLoading}
            >
              Email me a link
            </button>
          </div>

          {emailView === 'signin' ? (
            <form onSubmit={handleLogin} className="space-y-3">
              <InputField
                label="Email"
                type="email"
                inputMode="email"
                value={loginForm.values.email}
                onChange={(e) => loginForm.setValue('email', e.target.value)}
                onBlur={() => loginForm.setFieldTouched('email')}
                error={loginForm.errors.email}
                touched={loginForm.touched.email}
                leftIcon={<Mail className="h-4 w-4" />}
                required
                disabled={isLoading}
              />
              <InputField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={loginForm.values.password}
                onChange={(e) => loginForm.setValue('password', e.target.value)}
                onBlur={() => loginForm.setFieldTouched('password')}
                error={loginForm.errors.password}
                touched={loginForm.touched.password}
                leftIcon={<Lock className="h-4 w-4" />}
                rightIcon={
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="hover:text-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                  </button>
                }
                required
                disabled={isLoading}
              />
              <div className={`flex items-center gap-2 ${isNative ? 'justify-end' : 'justify-between'}`}>
                {!isNative && <span className="text-xs text-muted-foreground">You stay signed in until you sign out.</span>}
                <Button type="button" variant="link" className="p-0 h-auto text-xs" onClick={() => setCurrentStep('forgot-password')} disabled={isLoading}>
                  Forgot password?
                </Button>
              </div>
              <Button type="submit" className="w-full" disabled={isLoading || !loginForm.isValid}>
                {isLoading ? 'Signing in…' : 'Sign In'}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                New here?{' '}
                <button type="button" className="text-primary underline" onClick={() => setEmailView('signup')}>Create an account</button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleMagicLink} className="space-y-3">
              <p className="text-xs text-muted-foreground">
                No password to remember. We'll email you a sign-in code, and a link, that expire in 15 minutes.
              </p>
              <InputField
                label="Email"
                type="email"
                inputMode="email"
                placeholder="your@email.com"
                value={magicLinkForm.values.email}
                onChange={(e) => magicLinkForm.setValue('email', e.target.value)}
                onBlur={() => magicLinkForm.setFieldTouched('email')}
                error={magicLinkForm.errors.email}
                touched={magicLinkForm.touched.email}
                leftIcon={<Mail className="h-4 w-4" />}
                required
                disabled={isLoading}
              />
              <Button type="submit" className="w-full" disabled={isLoading || !magicLinkForm.isValid}>
                {isLoading ? 'Sending…' : 'Email me a code'}
              </Button>
              {/* Not a link: this is already true without clicking anything —
                  signInWithOtp creates the account itself if the email is new.
                  A button here would promise navigation that doesn't happen. */}
              <p className="text-center text-xs text-muted-foreground">
                New here? Entering your email above creates your account automatically.
              </p>
            </form>
          )}
        </div>
      )}

      {emailView === 'signup' && (
        <form onSubmit={handleSignUp} className="space-y-3">
          {/*
            No role question here (Option A): whether this becomes a client or
            helper account is decided once, right after, on /profile-creation
            — the chooser that already exists for exactly this. This form used
            to ask up front via a banner (#776); that banner is gone on
            purpose, not an oversight — see AuthFormProps.signupRole.
          */}
          <div className="grid grid-cols-2 gap-3">
            <InputField
              label="First Name"
              type="text"
              placeholder="First name"
              value={signupForm.values.firstName}
              onChange={(e) => signupForm.setValue('firstName', e.target.value)}
              onBlur={() => signupForm.setFieldTouched('firstName')}
              error={signupForm.errors.firstName}
              touched={signupForm.touched.firstName}
              leftIcon={<User className="h-4 w-4" />}
              required
              disabled={isLoading}
            />
            <InputField
              label="Last Name"
              type="text"
              placeholder="Last name"
              value={signupForm.values.lastName}
              onChange={(e) => signupForm.setValue('lastName', e.target.value)}
              onBlur={() => signupForm.setFieldTouched('lastName')}
              error={signupForm.errors.lastName}
              touched={signupForm.touched.lastName}
              leftIcon={<User className="h-4 w-4" />}
              required
              disabled={isLoading}
            />
          </div>
          <InputField
            label="Email"
            type="email"
            inputMode="email"
            placeholder="your@email.com"
            value={signupForm.values.email}
            onChange={(e) => signupForm.setValue('email', e.target.value)}
            onBlur={() => signupForm.setFieldTouched('email')}
            error={signupForm.errors.email}
            touched={signupForm.touched.email}
            leftIcon={<Mail className="h-4 w-4" />}
            required
            disabled={isLoading}
          />
          <InputField
            label="Phone (Optional)"
            type="tel"
            inputMode="tel"
            placeholder={config.phonePlaceholder}
            value={signupForm.values.phone}
            onChange={(e) => signupForm.setValue('phone', e.target.value)}
            onBlur={() => signupForm.setFieldTouched('phone')}
            error={signupForm.errors.phone}
            touched={signupForm.touched.phone}
            disabled={isLoading}
          />
          <InputField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
            value={signupForm.values.password}
            onChange={(e) => signupForm.setValue('password', e.target.value)}
            onBlur={() => signupForm.setFieldTouched('password')}
            error={signupForm.errors.password}
            touched={signupForm.touched.password}
            leftIcon={<Lock className="h-4 w-4" />}
            rightIcon={
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="hover:text-foreground">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
            helperText={`Use at least ${PASSWORD_MIN_LENGTH} characters`}
            required
            disabled={isLoading}
          />
          <InputField
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Re-enter your password"
            value={signupForm.values.confirmPassword}
            onChange={(e) => signupForm.setValue('confirmPassword', e.target.value)}
            onBlur={() => signupForm.setFieldTouched('confirmPassword')}
            error={signupForm.errors.confirmPassword}
            touched={signupForm.touched.confirmPassword}
            leftIcon={<Lock className="h-4 w-4" />}
            rightIcon={
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="hover:text-foreground">
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
            required
            disabled={isLoading}
          />
          <div className="flex items-start space-x-2">
            <Checkbox id="auth-form-terms" checked={agreeToTerms} onCheckedChange={(c) => setAgreeToTerms(c as boolean)} disabled={isLoading} />
            <label htmlFor="auth-form-terms" className="text-xs leading-5">
              I am 18 or older and I agree to the{' '}
              <a href="/terms" target="_blank" rel="noreferrer" className="underline hover:text-primary">Terms of Use</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer" className="underline hover:text-primary">Privacy Policy</a>
            </label>
          </div>
          <ConsentChoicesFields value={consents} onChange={setConsents} disabled={isLoading} idPrefix="signup-consent" />
          <div className="flex items-center gap-2 bg-muted/50 p-2.5 rounded-lg">
            <Shield className="h-4 w-4 text-primary shrink-0" />
            <p className="text-xs text-muted-foreground">We never sell your data. You can change these choices any time in your account.</p>
          </div>
          <Button type="submit" className="w-full" disabled={isLoading || !signupForm.isValid || !agreeToTerms || !consents.transferAbroad}>
            {isLoading
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin inline" />Creating account…</>
              : 'Create account'}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Already have an account?{' '}
            <button type="button" className="text-primary underline" onClick={() => setEmailView('signin')}>Sign in</button>
          </p>
        </form>
      )}

    </div>
  );

  const renderForgotPasswordScreen = () => (
    <div className="space-y-5">
      <div className="flex items-center space-x-3">
        <Button variant="ghost" size="sm" onClick={() => setCurrentStep('main')} disabled={isLoading}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="h-card">Reset your password</h2>
          <p className="text-sm text-muted-foreground">We'll send you a reset link</p>
        </div>
      </div>
      <form onSubmit={handleForgotPassword} className="space-y-4">
        <InputField
          label="Email"
          type="email"
          inputMode="email"
          placeholder="your@email.com"
          value={forgotPasswordForm.values.email}
          onChange={(e) => forgotPasswordForm.setValue('email', e.target.value)}
          onBlur={() => forgotPasswordForm.setFieldTouched('email')}
          error={forgotPasswordForm.errors.email}
          touched={forgotPasswordForm.touched.email}
          leftIcon={<Mail className="h-4 w-4" />}
          required
          disabled={isLoading}
        />
        <Button type="submit" className="w-full" size="lg" disabled={isLoading || !forgotPasswordForm.isValid}>
          {isLoading ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>
    </div>
  );

  const renderEmailCodeScreen = () => (
    <div className="space-y-5">
      <div className="flex items-center space-x-3">
        <Button variant="ghost" size="sm" onClick={() => setCurrentStep('main')} disabled={isLoading} aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="h-card">Check your email</h2>
          <p className="text-sm text-muted-foreground">We sent a code to {codeEmail}</p>
        </div>
      </div>
      <form onSubmit={handleEmailCode} className="space-y-4">
        <InputField
          label="Code from the email"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          value={emailCode}
          onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
          leftIcon={<Shield className="h-4 w-4" />}
          required
          disabled={isLoading}
          autoFocus
        />
        <Button type="submit" className="w-full" size="lg" disabled={isLoading || !EMAIL_CODE.test(emailCode)}>
          {isLoading ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
      <p className="text-center text-xs text-muted-foreground">
        {/* In the app the link opens the phone's browser, which cannot finish it. */}
        {!isNative && <>In this browser, you can tap the link in the email instead.{' '}</>}
        <button type="button" className="text-primary underline" onClick={resendEmailCode} disabled={isLoading}>
          Send a new code
        </button>
      </p>
    </div>
  );

  if (currentStep === 'email-code') return renderEmailCodeScreen();
  return currentStep === 'main' ? renderMainScreen() : renderForgotPasswordScreen();
}
